import _ from 'lodash';
import uuid from 'uuid';
import {
  FieldSet,
  TextCollectionField,
  TextCollectionFieldType,
  FormField,
} from '@scale/shared/types/params/textcollection';
import {
  TextCollectionAnnotation,
  FieldSetAnnotation,
} from '@scale/shared/types/response/textcollection';
import { getFieldSets, getFieldSetAnnotation, getFieldSetTitle, getForms } from './fieldSetUtils';

// Breadcrumb is used to keep track of FieldSet navigation. It stores the `field_id` of the FieldSet
// as the Breadcrumb `id`, which FieldSet response index, and first text value as the `title`.
export interface Breadcrumb {
  id: string;
  index: number;
}

interface CurrentFields {
  fields: TextCollectionField[];
  parentField?: FieldSet | FormField;
}

export function getCurrentFields(
  fields: TextCollectionField[],
  breadcrumbs: Breadcrumb[],
): CurrentFields {
  let currentFields = fields;
  let currentFieldSet: FieldSet | FormField | undefined;
  for (const breadcrumb of breadcrumbs) {
    const fieldSets: (FieldSet | FormField)[] = [
      ...getFieldSets(currentFields),
      ...getForms(currentFields),
    ];
    currentFieldSet = fieldSets.find(fieldSet => fieldSet.field_id === breadcrumb.id);
    if (!currentFieldSet) {
      return { fields: [] };
    }
    currentFields = currentFieldSet.fields;
  }
  return { fields: currentFields, parentField: currentFieldSet };
}

interface CurrentAnnotations {
  annotations: Record<string, TextCollectionAnnotation>;
  parentFieldSetAnnotation?: FieldSetAnnotation;
}

export function getCurrentAnnotations(
  annotations: Record<string, TextCollectionAnnotation>,
  breadcrumbs: Breadcrumb[],
): CurrentAnnotations {
  let currentAnnotations = annotations;
  let parentFieldSetAnnotation: FieldSetAnnotation | undefined;
  for (const breadcrumb of breadcrumbs) {
    const fieldSetAnnotation = currentAnnotations[breadcrumb.id];
    if (!fieldSetAnnotation) {
      return { annotations: {} };
    }
    if (fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
      throw new Error('breadcrumb should reference FieldSet or FormField by field_id');
    }
    if (breadcrumb.index >= fieldSetAnnotation.response.length) {
      throw new Error('breadcrumb should reference valid index of FieldSet or FormField response');
    }
    parentFieldSetAnnotation = fieldSetAnnotation;
    currentAnnotations = fieldSetAnnotation.response[breadcrumb.index];
  }
  return { annotations: currentAnnotations, parentFieldSetAnnotation };
}

export function getUpdateNextAnnotations(
  annotations: Record<string, TextCollectionAnnotation>,
  breadcrumbs: Breadcrumb[],
  newAnnotations: Record<string, TextCollectionAnnotation>,
): Record<string, TextCollectionAnnotation> {
  if (!breadcrumbs.length) {
    return newAnnotations;
  }
  const breadcrumb = breadcrumbs[0];
  const fieldSetAnnotation = getFieldSetAnnotation(annotations, breadcrumb.id);
  const nextFieldSetResponse = fieldSetAnnotation.response.slice();
  nextFieldSetResponse[breadcrumb.index] = getUpdateNextAnnotations(
    nextFieldSetResponse[breadcrumb.index],
    breadcrumbs.slice(1),
    newAnnotations,
  );
  const nextFieldSetUuids = fieldSetAnnotation.uuids?.slice();
  const nextFieldSetAnnotation: FieldSetAnnotation = {
    type: TextCollectionFieldType.FieldSet,
    field_id: breadcrumb.id,
    response: nextFieldSetResponse,
    uuids: nextFieldSetUuids,
  };
  return {
    ...annotations,
    [breadcrumb.id]: nextFieldSetAnnotation,
  };
}

export function getDeleteNextAnnotations(
  annotations: Record<string, TextCollectionAnnotation>,
  breadcrumbs: Breadcrumb[],
): Record<string, TextCollectionAnnotation> {
  if (!breadcrumbs.length) {
    throw new TypeError('delete only applies to nested FieldSet forms');
  }
  const breadcrumb = breadcrumbs[0];
  const fieldSetAnnotation = getFieldSetAnnotation(annotations, breadcrumb.id);
  const nextFieldSetResponse = fieldSetAnnotation.response.slice();
  const nextFieldSetUuids = fieldSetAnnotation.uuids?.slice();
  if (breadcrumbs.length === 1) {
    nextFieldSetResponse.splice(breadcrumb.index, 1);
    if (nextFieldSetUuids) {
      nextFieldSetUuids.splice(breadcrumb.index, 1);
    }
  } else {
    nextFieldSetResponse[breadcrumb.index] = getDeleteNextAnnotations(
      fieldSetAnnotation.response[breadcrumb.index],
      breadcrumbs.slice(1),
    );
  }
  const nextFieldSetAnnotation: FieldSetAnnotation = {
    type: TextCollectionFieldType.FieldSet,
    field_id: breadcrumb.id,
    response: nextFieldSetResponse,
    uuids: nextFieldSetUuids,
  };
  return {
    ...annotations,
    [breadcrumb.id]: nextFieldSetAnnotation,
  };
}

export function pathJoin(breadcrumbs: Breadcrumb[]): string {
  const path = breadcrumbs
    .map(breadcrumb => {
      return `${breadcrumb.id}/${breadcrumb.index}`;
    })
    .join('/');
  return path;
}

export function getPasteNextAnnotations(
  annotations: Record<string, TextCollectionAnnotation>,
  fromPath: Breadcrumb[],
  toPath: Breadcrumb[],
  cut: boolean,
): Record<string, TextCollectionAnnotation> {
  const isParentLevel = fromPath.length === toPath.length + 1;
  if (!isParentLevel) {
    return _.cloneDeep(annotations);
  }
  const breadcrumb = fromPath[fromPath.length - 1];
  const { annotations: fromAnnotations } = getCurrentAnnotations(annotations, fromPath);
  const { annotations: toAnnotations } = getCurrentAnnotations(annotations, toPath);
  const fieldSetAnnotation = getFieldSetAnnotation(toAnnotations, breadcrumb.id);
  if (fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
    throw new TypeError('invalid path expected fieldset');
  }
  const fieldSetResponse = fieldSetAnnotation.response.concat([_.cloneDeep(fromAnnotations)]);
  const nextFieldSetUuids =
    fieldSetAnnotation.uuids && fieldSetAnnotation.uuids.concat([uuid.v4()]);
  const nextToAnnotations = {
    ...toAnnotations,
    [breadcrumb.id]: {
      ...fieldSetAnnotation,
      response: fieldSetResponse,
      uuids: nextFieldSetUuids,
    },
  };
  const copyAnnotations = getUpdateNextAnnotations(annotations, toPath, nextToAnnotations);
  if (!cut) {
    return copyAnnotations;
  }
  return getDeleteNextAnnotations(copyAnnotations, fromPath);
}

export function getNestedFieldTitle(fields: TextCollectionField[], path: Breadcrumb[]): string {
  if (path.length === 0) {
    return '';
  }
  const breadcrumb = path[0];
  const field = fields.find(field => field.field_id === breadcrumb.id);
  if (!field) {
    return '';
  }
  if (path.length === 1) {
    return field.title;
  }
  if (field.type !== TextCollectionFieldType.FieldSet) {
    return '';
  }
  return getNestedFieldTitle(field.fields, path.slice(1));
}

export function getBreadcrumbTitle(
  fields: TextCollectionField[],
  annotations: Record<string, TextCollectionAnnotation>,
  path: Breadcrumb[],
): string {
  if (path.length === 0) {
    return '';
  }
  const breadcrumb = path[0];
  const fieldSet = fields.find(field => field.field_id === breadcrumb.id);
  if (!fieldSet || fieldSet.type !== TextCollectionFieldType.FieldSet) {
    return '';
  }
  const fieldSetAnnotation = annotations[breadcrumb.id];
  if (!fieldSetAnnotation || fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
    return '';
  }
  const fieldSetResponse = fieldSetAnnotation.response[breadcrumb.index] || {};
  if (path.length === 1) {
    return getFieldSetTitle(fieldSet, fieldSetResponse);
  }
  return getBreadcrumbTitle(fieldSet.fields, fieldSetResponse, path.slice(1));
}

export function getQuickAddResponses(
  annotations: Record<string, TextCollectionAnnotation>,
  path: Breadcrumb[],
  field: TextCollectionField,
): Record<string, TextCollectionAnnotation>[] {
  if (path.length === 0) {
    return [];
  }
  if (field.type !== TextCollectionFieldType.FieldSet) {
    return [];
  }
  const breadcrumb = path[0];
  const fieldSetAnnotation = annotations[breadcrumb.id];
  if (!fieldSetAnnotation || fieldSetAnnotation.type !== TextCollectionFieldType.FieldSet) {
    return [];
  }
  const currentResponse = fieldSetAnnotation.response[breadcrumb.index] || {};
  if (path.length >= 2) {
    return getQuickAddResponses(currentResponse, path.slice(1), field);
  }
  if (breadcrumb.index === 0) {
    return [];
  }
  const lastAnnotation = getFieldSetAnnotation(
    fieldSetAnnotation.response[breadcrumb.index - 1] || {},
    field.field_id,
  );
  const currentAnnotation = getFieldSetAnnotation(currentResponse, field.field_id);
  const currentTitles = new Set<string>();
  for (const response of currentAnnotation.response) {
    const title = getFieldSetTitle(field, response);
    currentTitles.add(title);
  }
  const quickAddResponses = lastAnnotation.response.filter(response => {
    const title = getFieldSetTitle(field, response);
    return !currentTitles.has(title);
  });
  return quickAddResponses;
}
